import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';

// Backend response structure
export interface BackendContact {
  id: string;
  businessAccountId: string;
  name: string | null;
  phoneNumber: string;
  email: string;
  // Backend now returns tag objects with id/name/color/description
  tags: any[];
  customFields: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

// Backend request structure for bulk import
export interface BulkImportContact {
  name: string;
  phoneNumber: string;
  email?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status?: string;
  notes?: string;
}

// Frontend Contact interface (for internal use)
export interface Contact {
  id: string;
  tenant_id: string;
  business_account_id: string;
  name: string | null;
  phone_number: string;
  profile_picture_url: string | null;
  type?: 'LEAD' | 'CUSTOMER';
  status: string;
  custom_fields: Record<string, any>;
  tags: string[];
  last_seen: string | null;
  campaign_opted_out?: boolean;
  campaignOptedOut?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateContactData {
  businessAccountId: string;
  phoneNumber: string;
  name?: string;
  email?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status?: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
}

export interface UpdateContactData {
  name?: string;
  phoneNumber?: string;
  email?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  type?: 'LEAD' | 'CUSTOMER';
  status?: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  notes?: string;
  campaignOptedOut?: boolean;
}

export interface ContactFilters {
  id?: string;
  business_account_id?: string;
  status?: string;
  search?: string;
  type?: 'LEAD' | 'CUSTOMER';
  tags?: string[];
  limit?: number;
  offset?: number;
  sort_by?: string;
  order?: 'asc' | 'desc';
  orderBy?: string;
  ascending?: boolean;
  source?: string;
  has_email?: boolean;
  has_phone?: boolean;
  created_from?: string; // ISO date or YYYY-MM-DD (inclusive)
  created_to?: string;   // ISO date or YYYY-MM-DD (inclusive end-of-day)
}

export interface PaginatedContactsResponse {
  success: boolean;
  data: BackendContact[];
  count: number;
  totalCount: number;
  filteredTotalCount?: number;
  hasMore: boolean;
}

export const contactsApi = {
  /**
   * Get customer contact IDs (contacts with orders)
   */
  async getCustomerIds(businessAccountId: string): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>(
        `/contacts/customers?businessAccountId=${businessAccountId}`
      );

      if (response.error) {
        console.error('Error fetching customer IDs:', response.error);
        return [];
      }

      // API client already extracts data from { success, data } response
      // response.data should be the array of customer IDs
      if (Array.isArray(response.data)) {
        return response.data;
      }

      // Fallback: handle unexpected response format
      console.warn('Unexpected response format for getCustomerIds:', response);
      return [];
    } catch (error) {
      console.error('Exception in getCustomerIds:', error);
      return [];
    }
  },

  /**
   * Get all contacts with optional filtering
   */
  async getAll(filters: ContactFilters = {}) {
    const params: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params[key] = value.join(',');
        } else {
          params[key] = String(value);
        }
      }
    });

    return apiClient.get<PaginatedContactsResponse>(API_ENDPOINTS.CONTACTS, { params });
  },

  /**
   * Get a single contact by ID
   */
  async getById(id: string) {
    return apiClient.get<BackendContact>(`${API_ENDPOINTS.CONTACTS}/${id}`);
  },

  /**
   * Create a new contact
   */
  async create(data: CreateContactData) {
    return apiClient.post<BackendContact>(API_ENDPOINTS.CONTACTS, data);
  },

  /**
   * Update a contact
   */
  async update(id: string, data: UpdateContactData) {
    return apiClient.put<BackendContact>(`${API_ENDPOINTS.CONTACTS}/${id}`, data);
  },

  /**
   * Delete a contact
   */
  async delete(id: string) {
    return apiClient.delete(`${API_ENDPOINTS.CONTACTS}/${id}`);
  },

  /**
   * Update contact flow settings
   */
  async updateFlowSettings(id: string, businessAccountId: string, isFlowsPaused: boolean) {
    return apiClient.post<{ success: boolean; data: BackendContact }>(
      `${API_ENDPOINTS.CONTACTS}/${id}/flow-settings`,
      {
        businessAccountId,
        isFlowsPaused,
      }
    );
  },

  /**
   * Search contacts
   */
  async search(query: string, businessAccountId?: string) {
    const filters: ContactFilters = {
      search: query,
      business_account_id: businessAccountId,
    };
    return this.getAll(filters);
  },

  /**
   * Get contacts by business account
   */
  async getByBusinessAccount(businessAccountId: string, filters: Omit<ContactFilters, 'business_account_id'> = {}) {
    return this.getAll({ ...filters, business_account_id: businessAccountId });
  },

  /**
   * Bulk import contacts
   */
  async bulkImport(businessAccountId: string, contacts: BulkImportContact[], fileUrl?: string) {
    return apiClient.post(`${API_ENDPOINTS.CONTACTS}/bulk-import`, {
      businessAccountId,
      contacts,
      fileUrl,
    });
  },

  /**
   * Get S3 presigned URL for bulk import file
   */
  async getUploadUrl(businessAccountId: string, fileName: string, fileType: string) {
    return apiClient.post<{ uploadUrl: string; fileUrl: string; key: string }>(
      `${API_ENDPOINTS.CONTACTS}/upload-url`,
      {
        businessAccountId,
        fileName,
        fileType,
      }
    );
  },

  /**
   * Batch lookup of tags for many contacts. Returns { [contactId]: Tag[] }.
   */
  async getTagsByContactIds(businessAccountId: string, contactIds: string[]) {
    if (!contactIds || contactIds.length === 0) {
      return { data: { success: true, data: {} as Record<string, Array<{ id: string; name: string; color: string; description: string | null }>> }, error: null, success: true } as any;
    }
    return apiClient.get<{ success: boolean; data: Record<string, Array<{ id: string; name: string; color: string; description: string | null }>> }>(
      `${API_ENDPOINTS.CONTACTS}/tags-by-contacts`,
      {
        params: {
          business_account_id: businessAccountId,
          contact_ids: contactIds.join(','),
        },
      }
    );
  },

  /**
   * Get all unique tags for a business account
   */
  async getTags(businessAccountId: string, type?: 'LEAD' | 'CUSTOMER') {
    return apiClient.get<{ success: boolean; data: Array<{ id: string; name: string; color: string; description: string; count: number }> }>(
      `${API_ENDPOINTS.CONTACTS}/tags`,
      { params: { business_account_id: businessAccountId, type } }
    );
  },

  /**
   * Get paginated tags for a business account
   */
  async getTagsPaged(businessAccountId: string, { limit = 25, offset = 0, search, type }: { limit?: number; offset?: number; search?: string; type?: 'LEAD' | 'CUSTOMER' } = {}) {
    return apiClient.get<{ success: boolean; data: Array<{ id: string; name: string; color: string; description: string; count: number }> }>(
      `${API_ENDPOINTS.CONTACTS}/tags`,
      {
        params: {
          business_account_id: businessAccountId,
          limit,
          offset,
          search: search && search.trim().length > 0 ? search : undefined,
          type
        }
      }
    );
  },

  /**
   * Create a new tag and optionally assign to contacts
   */
  async createTag(businessAccountId: string, name: string, color: string = "#3B82F6", description?: string, contactIds: string[] = []) {
    return apiClient.post(`${API_ENDPOINTS.CONTACTS}/tags`, {
      business_account_id: businessAccountId,
      name,
      color,
      description,
      contact_ids: contactIds,
    });
  },

  /**
   * Delete a tag from all contacts
   */
  async deleteTag(businessAccountId: string, tagId: string) {
    return apiClient.delete(`${API_ENDPOINTS.CONTACTS}/tags`, {
      body: {
        business_account_id: businessAccountId,
        tag_id: tagId,
      },
    });
  },

  /**
   * Bulk add tags to contacts
   */
  async bulkAddTagsToContacts(businessAccountId: string, contactIds: string[], tagIds: string[]) {
    return apiClient.post(`${API_ENDPOINTS.CONTACTS}/bulk/tags`, {
      businessAccountId,
      contactIds,
      tagIds,
    });
  },

  /**
   * Bulk remove tags from contacts
   */
  async bulkRemoveTagsFromContacts(businessAccountId: string, contactIds: string[], tagIds: string[]) {
    return apiClient.delete(`${API_ENDPOINTS.CONTACTS}/bulk/tags`, {
      body: {
        businessAccountId,
        contactIds,
        tagIds,
      },
    });
  },

  /**
   * Update a tag
   */
  async updateTag(businessAccountId: string, tagId: string, name: string, color: string, description?: string) {
    return apiClient.put(`${API_ENDPOINTS.CONTACTS}/tags/${tagId}`, {
      business_account_id: businessAccountId,
      name,
      color,
      description,
    });
  },

  /**
   * Block a contact
   */
  async block(id: string, businessAccountId: string) {
    return apiClient.post<{ success: boolean; data: BackendContact }>(
      `${API_ENDPOINTS.CONTACTS}/${id}/block`,
      {},
      { params: { businessAccountId } }
    );
  },

  /**
   * Unblock a contact
   */
  async unblock(id: string, businessAccountId: string) {
    return apiClient.post<{ success: boolean; data: BackendContact }>(
      `${API_ENDPOINTS.CONTACTS}/${id}/unblock`,
      {},
      { params: { businessAccountId } }
    );
  },

  /**
   * Get blocked contacts
   */
  async getBlocked(businessAccountId: string) {
    return apiClient.get<{ success: boolean; data: BackendContact[]; count: number; totalCount: number }>(
      `${API_ENDPOINTS.CONTACTS}/blocked`,
      { params: { businessAccountId } }
    );
  },

  /**
   * Get contact statistics
   */
  /**
   * Get contact statistics
   */
  async getStats(businessAccountId: string, type?: string) {
    const params: any = { business_account_id: businessAccountId };
    if (type) params.type = type;

    return apiClient.get<{
      success: boolean;
      data: {
        newContactsToday: number;
        unsubscribed: number;
        noInteractivity30Days: number;
        totalContacts: number;
      };
    }>(`${API_ENDPOINTS.CONTACTS}/stats`, { params });
  },

  /**
   * Check contact limit before import
   */
  async checkLimit(businessAccountId: string, contactsToImport: number) {
    return apiClient.get<{
      currentCount: number;
      limit: number | null;
      contactsToImport: number;
      totalAfterImport: number;
      wouldExceed: boolean;
      allowed: boolean;
      remaining: number | null;
      message: string | null;
    }>(`${API_ENDPOINTS.CONTACTS}/check-limit`, {
      params: {
        businessAccountId,
        contactsToImport,
      },
    });
  },

  /**
   * Check tag limit before import
   */
  async checkTagLimit(businessAccountId: string, tagNames: string[]) {
    return apiClient.get<{
      currentTagCount: number;
      limit: number | null;
      existingTags: string[];
      newTags: string[];
      newTagsCount: number;
      totalAfterImport: number;
      wouldExceed: boolean;
      allowed: boolean;
      remaining: number | null;
      message: string | null;
    }>(`${API_ENDPOINTS.CONTACTS}/check-tag-limit`, {
      params: {
        businessAccountId,
        tagNames,
      },
    });
  },

  /**
   * Get contact growth stats (monthly)
   */
  async getGrowthStats(businessAccountId: string, year: number, type?: string) {
    const params: any = {
      business_account_id: businessAccountId,
      year,
    };
    if (type) params.type = type;

    return apiClient.get<{
      success: boolean;
      data: number[];
    }>(`${API_ENDPOINTS.CONTACTS}/growth`, { params });
  },

  /**
   * Initiate Bolna AI voice calls to selected contacts.
   */
  async bulkInitiateCalls(businessAccountId: string, contactIds: string[]) {
    return apiClient.post<{
      success: boolean;
      data: {
        initiated: number;
        failed: number;
        skipped: number;
        calls: Array<{
          contactId: string;
          contactName: string;
          phone: string;
          executionId: string;
          status: string;
        }>;
        errors?: Array<{ error: string; code?: string }>;
      };
      message: string;
    }>(`${API_ENDPOINTS.CONTACTS}/bulk/call`, {
      businessAccountId,
      contactIds,
    });
  },

  /**
   * Queue a background Leads/Customers export (xlsx|csv). Requires plan feature `contact_export`.
   * Completion arrives via socket notification CONTACT_EXPORT_COMPLETED with a short-lived fileUrl.
   */
  async requestExport(body: Record<string, unknown>): Promise<{
    ok: boolean;
    jobId?: string;
    error?: string;
  }> {
    const res = await apiClient.post<{ jobId: string }>(
      `${API_ENDPOINTS.CONTACTS}/export`,
      body
    );
    if (res.error || res.success === false) {
      return { ok: false, error: res.error || 'Failed to start export' };
    }
    const jobId = (res.data as any)?.jobId || (res as any)?.jobId;
    if (!jobId) {
      return { ok: false, error: 'Export job id missing' };
    }
    return { ok: true, jobId };
  },

  async getExportStatus(jobId: string): Promise<{
    ok: boolean;
    status?: string;
    fileUrl?: string | null;
    format?: string | null;
    filename?: string | null;
    error?: string;
  }> {
    const res = await apiClient.get<{
      jobId: string;
      status: string;
      fileUrl?: string | null;
      format?: string | null;
      filename?: string | null;
      error?: string | null;
    }>(`${API_ENDPOINTS.CONTACTS}/export/${jobId}`);
    if (res.error || res.success === false) {
      return { ok: false, error: res.error || 'Failed to get export status' };
    }
    const data = (res.data || res) as any;
    return {
      ok: true,
      status: data.status,
      fileUrl: data.fileUrl ?? null,
      format: data.format ?? null,
      filename: data.filename ?? null,
      error: data.error || undefined,
    };
  },

  /**
   * @deprecated Prefer requestExport (background). Kept for compatibility.
   */
  async exportCsv(body: Record<string, unknown>): Promise<{
    ok: boolean;
    error?: string;
    truncated?: boolean;
    jobId?: string;
  }> {
    return this.requestExport(body);
  },

};
